import React, { useMemo, useState } from 'react';
import { t } from '../../onboarding/strings';
import { metaForField, defaultPages } from '../../onboarding/catalog';
import { diffItems, diffSummary } from '../../onboarding/diff';

const DEFAULT_ID = '__ios_default__';

function itemsFor(id, versions) {
  if (id === DEFAULT_ID) return defaultPages.map((p, i) => ({ uid: 'd' + i, page: p, enabled: true }));
  const v = versions.find((x) => x.id === id);
  return v ? v.items : [];
}

function Cell({ item, highlight }) {
  if (!item) return <div className="cmp-cell cmp-empty">—</div>;
  const meta = metaForField(item.page.field);
  return (
    <div className={`cmp-cell ${highlight || ''} ${item.enabled ? '' : 'cmp-disabled'}`}>
      <span className="cmp-emoji">{meta.emoji}</span>
      <span className="cmp-main">
        <span className="cmp-title">{t(item.page.title) || meta.label}</span>
        <span className="cmp-type">{item.page.content && item.page.content.type}</span>
      </span>
      {!item.enabled && <span className="cmp-off">off</span>}
    </div>
  );
}

const MID = { same: '=', changed: '≠', 'a-only': '◀', 'b-only': '▶' };

export default function CompareView({ versions, onClose, initialA, initialB }) {
  const options = useMemo(
    () => [...versions.map((v) => ({ id: v.id, name: v.name })), { id: DEFAULT_ID, name: 'iOS default (bundled)' }],
    [versions]
  );
  const [aId, setAId] = useState(initialA || options[0].id);
  const [bId, setBId] = useState(initialB || (options[1] ? options[1].id : options[0].id));

  const aItems = useMemo(() => itemsFor(aId, versions), [aId, versions]);
  const bItems = useMemo(() => itemsFor(bId, versions), [bId, versions]);
  const rows = useMemo(() => diffItems(aItems, bItems), [aItems, bItems]);
  const sum = useMemo(() => diffSummary(rows), [rows]);

  const aName = options.find((o) => o.id === aId)?.name || 'A';
  const bName = options.find((o) => o.id === bId)?.name || 'B';

  return (
    <div className="ob-modal-overlay" onClick={onClose}>
      <div className="cmp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ob-modal-head">
          <h3>⇄ Compare versions</h3>
          <button className="ob-modal-x" onClick={onClose}>✕</button>
        </div>

        <div className="cmp-pickers">
          <select className="cmp-select" value={aId} onChange={(e) => setAId(e.target.value)}>
            {options.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
          </select>
          <span className="cmp-vs">vs</span>
          <select className="cmp-select" value={bId} onChange={(e) => setBId(e.target.value)}>
            {options.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
          </select>
        </div>

        <div className="cmp-summary">
          <span className="cmp-chip same">{sum.same} identical</span>
          <span className="cmp-chip changed">{sum.changed} changed</span>
          <span className="cmp-chip only">{sum['a-only']} only in “{aName}”</span>
          <span className="cmp-chip only">{sum['b-only']} only in “{bName}”</span>
        </div>

        <div className="cmp-table">
          <div className="cmp-thead">
            <div className="cmp-pos">#</div>
            <div className="cmp-col-head">{aName} <span>({aItems.length})</span></div>
            <div className="cmp-mid" />
            <div className="cmp-col-head">{bName} <span>({bItems.length})</span></div>
            <div className="cmp-pos">#</div>
          </div>
          <div className="cmp-rows">
            {rows.map((r, idx) => (
              <div key={idx} className={`cmp-row ${r.status}`}>
                <div className="cmp-pos">{r.ai != null ? r.ai + 1 : ''}</div>
                <Cell item={r.a} highlight={r.status === 'changed' ? 'hl-changed' : r.status === 'a-only' ? 'hl-only' : ''} />
                <div className={`cmp-mid ${r.status}`}>{MID[r.status]}</div>
                <Cell item={r.b} highlight={r.status === 'changed' ? 'hl-changed' : r.status === 'b-only' ? 'hl-only' : ''} />
                <div className="cmp-pos">{r.bj != null ? r.bj + 1 : ''}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
